import React, { useState, useContext } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';

import { Button } from '$partials/ui';
import { formatCurrency, formatRentPrice } from '$utilities/data';
import { MediaGalleryContext } from '$partials/global/media-gallery.jsx';
import { PropertyAreaText, PropertyPriceText } from '$partials/properties';
import { ShareButtonModal } from '$partials/ui/share-button.jsx';
import { useExchangeRate } from '$contexts/exchange-rate';
import TeamMemberEnquireDialog from './enquire-dialog';

/* ========================================================================= */
/* Helpers */
/* ========================================================================= */

const hasHoverSupport = () => {
    try {
        return matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch {
        // Assume that if browser too old to support matchMedia it's likely not a touch device
        return true;
    }
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/properties').PropertyInfoCard}
 */
const PropertyInfoCard = ({
    __typename,
    coordinates,
    displayAddress,
    documents,
    images,
    title,
    price: priceProp,
    secondPrice: secondPriceProp,
    priceCurrency: priceCurrencyProp,
    priceTypeLabel,
    location,
    bedrooms,
    bathrooms,
    tenure,
    propertyReferenceId,
    livingAreaMeters,
    outdoorAreaMeters,
    priceRentPeriod: priceRentPeriodProp,
    setShowMap,
    propertiesCount,
    externalId,
    office,
}) => {
    const { currency, convertToCurrency } = useExchangeRate() || {};

    const { setGalleryInitialId } = useContext(MediaGalleryContext);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const hasHover = hasHoverSupport();

    const price = priceProp;
    const secondPrice = secondPriceProp;
    const propertyType = __typename;

    const priceCurrency = priceCurrencyProp;
    const priceRentPeriod = priceRentPeriodProp;

    // Check if office has valid recipients based on property type
    const hasValidRecipients = office && (
        (propertyType === 'RentalPropertyDetailPage' && (office.lettingsEnquiryRecipients?.length > 0 || office.generalEnquiryRecipients?.length > 0)) ||
        (propertyType === 'SalePropertyDetailPage' && (office.salesEnquiryRecipients?.length > 0 || office.generalEnquiryRecipients?.length > 0)) ||
        (propertyType === 'NewHomeDetailPage' && (office.newHomesEnquiryRecipients?.length > 0 || office.generalEnquiryRecipients?.length > 0))
    );

    /**
     * @param {string | number | undefined} v
     */
    const shouldRenderStringOrNumber = (v) => {
        if (v) {
            return true;
        }
        return false;
    };

    return (
        <section className='theme-light-alt'>
            <div className='bg-main-background'>
                <div className='layout-container layout-site-outer-padding gap-y-32 md:gap-y-0 flex flex-col py-16 sm:py-40 md:py-48'>
                    <div className='flex gap-y-32 flex-col sm:flex-row col-wrapper'>
                        <div className='sm:w-7/12 md:w-8/12 col-padding flex flex-col'>
                            <div className='md:min-h-[210px]'>
                                {displayAddress && (
                                    <p className='type-style-eyebrow mb-8'>
                                        {displayAddress}
                                    </p>
                                )}
                                <h1 className='type-style-2'>
                                    {title}
                                </h1>
                            </div>
                        </div>
                        <div className='sm:w-5/12 md:w-4/12 flex flex-col justify-between col-padding'>
                            {/* Prefix */}
                            {(priceTypeLabel && priceTypeLabel !== 'Price On Application') && (
                                <p className='type-style-eyebrow mb-8'>
                                    {priceTypeLabel}
                                </p>
                            )}
                            {!priceTypeLabel && (
                                <div className='sm:mb-[25px]' />
                            )}
                            <div className='inline-flex gap-8 items-baseline'>
                                <p className='type-style-2'>
                                    <PropertyPriceText
                                        pageType={propertyType}
                                        price={price}
                                        priceCurrency={priceCurrency}
                                        priceRentPeriod={priceRentPeriod}
                                        priceTypeLabel={priceTypeLabel}
                                        secondPrice={secondPrice} />
                                </p>

                                {(price && priceTypeLabel !== 'Price On Application') && (
                                    <Tooltip.Provider delayDuration={!hasHover ? 0 : 200}>
                                        <Tooltip.Root
                                            onOpenChange={setTooltipOpen}
                                            open={tooltipOpen}>
                                            <Tooltip.Trigger
                                                className='items-start'
                                                onClick={(e) => {
                                                    if (!hasHover) {
                                                        e.preventDefault();
                                                        setTooltipOpen(true);
                                                    }
                                                }}>
                                                <svg
                                                    aria-hidden={true}
                                                    fill='none'
                                                    height='20'
                                                    viewBox='0 0 20 20'
                                                    width='20'
                                                    xmlns='http://www.w3.org/2000/svg'>
                                                    <path
                                                        d='M10 .4a9.6 9.6 0 0 1 9.6 9.6 9.6 9.6 0 0 1-9.6 9.6A9.6 9.6 0 0 1 .4 10 9.6 9.6 0 0 1 10 .4Z'
                                                        stroke='#242320'
                                                        strokeWidth='.8' />
                                                    <path
                                                        d='M7.894 14.167a.38.38 0 0 1-.28-.114.38.38 0 0 1-.114-.28v-.157a.4.4 0 0 1 .036-.164.5.5 0 0 1 .098-.146q.534-.465.825-.818.291-.355.291-1.119 0-.135-.013-.262a1.3 1.3 0 0 0-.054-.258h-.844a.33.33 0 0 1-.24-.099.33.33 0 0 1-.099-.24q0-.139.1-.239a.33.33 0 0 1 .239-.099h.504q-.486-.619-.665-1.1a2.6 2.6 0 0 1-.178-.908q0-1.077.73-1.808a2.42 2.42 0 0 1 1.783-.731q.697 0 1.261.33t.914.916a.3.3 0 0 1 .032.27.3.3 0 0 1-.179.2.3.3 0 0 1-.243.01.4.4 0 0 1-.198-.163 2 2 0 0 0-.68-.647 1.8 1.8 0 0 0-.907-.239q-.771 0-1.303.532a1.76 1.76 0 0 0-.533 1.291q0 .478.25.946.251.467.727 1.101h1.81q.14 0 .239.1.1.099.1.239a.33.33 0 0 1-.1.24.33.33 0 0 1-.24.098h-1.59q.03.164.043.288.01.125.011.297 0 .578-.239 1.106t-.698.95h2.942q.369 0 .612-.192.243-.191.377-.574a.4.4 0 0 1 .133-.195.35.35 0 0 1 .219-.068q.134 0 .243.1a.3.3 0 0 1 .108.233q0 .59-.587.98-.587.393-1.105.393z'
                                                        fill='#242320' />
                                                </svg>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content className='bg-button-primary-filled-background text-button-primary-filled-text rounded min-w-[145px]'>
                                                    <div className='px-16 py-12 flex flex-col gap-4 type-style-eyebrow'>
                                                        {['GBP', 'EUR', 'USD', 'ILS'].map((c) => {
                                                            if (c === currency) { return null; }
                                                            const convertedPrice = convertToCurrency ? convertToCurrency({
                                                                from: priceCurrency || 'GBP',
                                                                to: c,
                                                                amount: price,
                                                            }) : price;
                                                            const convertedSecondPrice = (convertToCurrency && secondPrice) ? convertToCurrency({
                                                                from: priceCurrency || 'GBP',
                                                                to: c,
                                                                amount: secondPrice,
                                                            }) : null;
                                                            let formattedPrice = '';
                                                            if (propertyType === 'RentalPropertyDetailPage') {
                                                                if (convertedSecondPrice) {
                                                                    formattedPrice = formatRentPrice(convertedPrice, c, priceRentPeriod, { includeSuffix: false });
                                                                    formattedPrice += ` – ${formatRentPrice(convertedSecondPrice, c, priceRentPeriod)}`;
                                                                } else {
                                                                    formattedPrice = formatRentPrice(convertedPrice, c, priceRentPeriod);
                                                                }
                                                            } else {
                                                                formattedPrice = formatCurrency(convertedPrice, c);
                                                                if (convertedSecondPrice) {
                                                                    formattedPrice += ` – ${formatCurrency(convertedSecondPrice, c)}`;
                                                                }
                                                            }
                                                            return (
                                                                <p key={c}>
                                                                    {formattedPrice}
                                                                </p>
                                                            );
                                                        })}
                                                    </div>
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                )}
                            </div>
                            <div className='grid grid-cols-3 gap-16 sm:gap-x-10 md:gap-x-8 sm:gap-y-8 md:gap-y-24 mt-24 md:mb-32'>
                                {shouldRenderStringOrNumber(propertiesCount) && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Properties
                                        </p>
                                        <p className='type-style-body-r'>
                                            {propertiesCount}
                                        </p>
                                    </div>
                                )}
                                {shouldRenderStringOrNumber(bedrooms) && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Bed
                                        </p>
                                        <p className='type-style-body-r'>
                                            {bedrooms}{__typename === 'NewHomeDetailPage'? '+':''}
                                        </p>
                                    </div>
                                )}
                                {shouldRenderStringOrNumber(bathrooms) && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Bath
                                        </p>
                                        <p className='type-style-body-r'>
                                            {bathrooms}{__typename === 'NewHomeDetailPage'? '+':''}
                                        </p>
                                    </div>
                                )}
                                {tenure?.name && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Tenure
                                        </p>
                                        <p className='type-style-body-r'>
                                            {tenure.name}
                                        </p>
                                    </div>
                                )}
                                {livingAreaMeters && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            INT.
                                        </p>
                                        <p className='type-style-body-r'>
                                            <PropertyAreaText
                                                areaMeters={livingAreaMeters}
                                                suffix={__typename === 'NewHomeDetailPage' ? '+' : ''} />
                                        </p>
                                    </div>
                                )}
                                {outdoorAreaMeters > 0 && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            EXT.
                                        </p>
                                        <p className='type-style-body-r'>
                                            <PropertyAreaText
                                                areaMeters={outdoorAreaMeters}
                                                suffix={__typename === 'NewHomeDetailPage' ? '+' : ''} />
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='hidden sm:flex gap-y-32 flex-col sm:flex-row col-wrapper'>
                        <div className='sm:w-7/12 md:w-8/12 col-padding flex flex-col'>
                            {/* Desktop Additional info buttons */}
                            <div className='flex flex-wrap gap-8 mb-16 theme-light-alt'>
                                {setShowMap && coordinates &&(
                                    <Button
                                        className='button-secondary-filled min-w-[115px] rounded type-style-eyebrow'
                                        onClick={() => setShowMap(true)}>
                                        {'Map'}
                                    </Button>
                                )}
                                {images?.length > 0 && (
                                    <Button
                                        className='button-secondary-filled min-w-[115px] rounded type-style-eyebrow'
                                        onClick={() => setGalleryInitialId(images?.[0]?.media?.image?.id)}>
                                        {images?.length} Photos
                                    </Button>
                                )}
                                {documents?.length > 0 && (
                                    documents.map((doc, index) => (
                                        <Button
                                            as='a'
                                            className='button-secondary-filled min-w-[115px] rounded type-style-eyebrow'
                                            href={doc?.url || doc?.document?.url}
                                            key={index}>
                                            {doc?.label}
                                        </Button>
                                    ))
                                )}

                            </div>
                            {propertyReferenceId && (
                                <p className='type-style-eyebrow mb-8'>
                                    {`Property Ref: ${propertyReferenceId}`}
                                </p>
                            )}
                        </div>
                        <div className='sm:w-5/12 md:w-4/12 flex flex-row gap-x-8 col-padding'>
                            {externalId && hasValidRecipients && (
                                <div className='hidden sm:block grow'>
                                    <Button
                                        className='min-w-[127px] w-full button-brand-filled py-13 px-35 rounded type-style-eyebrow'
                                        onClick={() => setIsDialogOpen(true)}>
                                        Enquire
                                    </Button>
                                </div>
                            )}
                            <ShareButtonModal
                                height='h-[44px]'
                                propertyRef={propertyReferenceId}
                                title={title}
                                url={location}
                                width='w-[44px]'/>
                        </div>
                    </div>
                    <div className='block sm:hidden w-full mt-24 theme-light-alt'>
                        <div className='flex flex-col sm:flex-row-reverse gap-x-8 items-center sm:mb-16 sm:justify-end'>
                            <div className='w-full flex gap-8'>
                                {externalId && hasValidRecipients && (
                                    <Button
                                        className='w-full bg-button-brand-filled-background py-13 px-35 rounded text-button-brand-filled-text type-style-eyebrow'
                                        onClick={() => setIsDialogOpen(true)}>
                                        Enquire
                                    </Button>
                                )}
                                <ShareButtonModal
                                    height='h-[44px]'
                                    propertyRef={propertyReferenceId}
                                    title={title}
                                    url={location}
                                    width='w-[44px]'/>
                            </div>
                            <div className='grid grid-cols-2 w-full mt-8 sm:flex gap-8'>
                                {setShowMap && (
                                    <Button
                                        className='bg-button-secondary-filled-background text-center py-13 px-35 rounded text-button-secondary-filled-text type-style-eyebrow'
                                        onClick={() => setShowMap(true)}>
                                        {'Map'}
                                    </Button>
                                )}
                                {images?.length > 0 && (
                                    <Button
                                        className='bg-button-secondary-filled-background text-center py-13 px-35 rounded text-button-secondary-filled-text type-style-eyebrow'
                                        onClick={() => setGalleryInitialId(images?.[0]?.media?.image?.id)}>
                                        {images?.length} Photos
                                    </Button>
                                )}
                                {documents?.length > 0 && (
                                    documents.map((doc, index) => (
                                        <Button
                                            as='a'
                                            className='bg-button-secondary-filled-background text-center py-13 px-35 rounded text-button-secondary-filled-text type-style-eyebrow'
                                            href={doc?.url || doc?.document?.url}
                                            key={index}>
                                            {doc?.label}
                                        </Button>
                                    ))
                                )}
                            </div>
                        </div>

                        <p className='type-style-eyebrow mt-8'>
                            Property Ref: {propertyReferenceId}
                        </p>
                    </div>
                </div>
            </div>
            {isDialogOpen && (
                <TeamMemberEnquireDialog
                    closeDialog={() => setIsDialogOpen(false)}
                    isOpen={isDialogOpen}/>
            )}
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PropertyInfoCard;
